/**
 * Filtros y orden comerciales de registros (Registros y detalle de lista), en el cliente.
 * Nombres alineados al contrato de la API (`status`, `offers`, `unseen_inquiries_count`,
 * `last_activity_at`, `leads_count`, `interests_count`, `inquiries_count`)
 * para pasarlos al back cuando llegue la paginación.
 * @see docs/maps/registros-commercial-filters.md
 */
import { COMMERCIAL_STATUS_META } from './leadsConfig';

/** Orden del pipeline, de más avanzada a menos. */
export const COMMERCIAL_STAGE_ORDER = [
  'cerrado', 'reservado', 'ofertado', 'en_seguimiento', 'contactado', 'nuevo', 'con_consultas', 'descartado', 'sin_interes',
];

/** Orden en que se listan las etapas en el filtro (como el pipeline de Leads). */
export const COMMERCIAL_STATUS_FILTER_OPTIONS = [
  'sin_interes', 'con_consultas', 'nuevo', 'contactado', 'en_seguimiento', 'ofertado', 'reservado', 'cerrado', 'descartado',
].map((value) => ({ value, label: COMMERCIAL_STATUS_META[value]?.label || value, color: COMMERCIAL_STATUS_META[value]?.color || null }));

export const STALE_DAYS_OPTIONS = [7, 15, 30];

export const DEFAULT_COMMERCIAL_FILTERS = Object.freeze({
  status: [],
  withOffers: false,
  unattended: false,
  staleDays: null,
});

export const COMMERCIAL_SORT_OPTIONS = [
  { value: 'interested-desc', label: 'Más interesados' },
  { value: 'commercial-inquiries-desc', label: 'Más consultados' },
  { value: 'commercial-inquiries-asc', label: 'Menos consultados' },
  { value: 'activity-desc', label: 'Actividad reciente' },
  { value: 'stage-desc', label: 'Etapa más avanzada' },
];

export const isCommercialSort = (sortBy) => COMMERCIAL_SORT_OPTIONS.some((o) => o.value === sortBy);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const DAY_MS = 86400000;

/**
 * Bloque comercial del registro. Lote con UF → `commercial_rollup` (lote + UF).
 * CAD y lote sin UF → `commercial_summary` (o el rollup si es lo único que vino).
 * Claves faltantes del bloque mínimo → 0 / {} / null. Sin bloque → null.
 */
export function getRecordCommercialBlock(row) {
  if (!row || typeof row !== 'object') return null;
  const units = Array.isArray(row.units) ? row.units : [];
  const raw = units.length > 0
    ? (row.commercial_rollup || null)
    : (row.commercial_summary || row.commercial_rollup || null);
  if (!raw || typeof raw !== 'object') return null;
  const offers = raw.offers && typeof raw.offers === 'object' ? raw.offers : {};
  return {
    status: COMMERCIAL_STATUS_META[raw.status] ? raw.status : 'sin_interes',
    leads_count: num(raw.leads_count),
    interests_count: num(raw.interests_count),
    inquiries_count: num(raw.inquiries_count),
    unseen_inquiries_count: num(raw.unseen_inquiries_count),
    offers: { USD: offers.USD ?? null, ARS: offers.ARS ?? null },
    last_activity_at: raw.last_activity_at || null,
    units_by_status: raw.units_by_status && typeof raw.units_by_status === 'object' ? raw.units_by_status : {},
  };
}

/** Etapas presentes en el registro: la del bloque, la del lote y las de sus UF. */
function recordStatuses(row, block) {
  const set = new Set([block.status]);
  Object.entries(block.units_by_status).forEach(([k, v]) => { if (num(v) > 0) set.add(k); });
  if (Array.isArray(row?.units) && row.units.length && row.commercial_summary?.status) {
    set.add(row.commercial_summary.status);
  }
  return set;
}

export function countActiveCommercialFilters(f) {
  if (!f) return 0;
  return (f.status?.length ? 1 : 0)
    + (f.withOffers ? 1 : 0)
    + (f.unattended ? 1 : 0)
    + (f.staleDays ? 1 : 0);
}

/**
 * Sin filtros activos → true. Con filtros, un registro sin bloque no matchea.
 * «Sin movimiento hace más de N días»: necesita `last_activity_at`. `sin_interes`
 * (sin fecha) no entra: no hay nada que se haya frenado.
 */
export function recordMatchesCommercialFilters(row, f, now = Date.now()) {
  if (!countActiveCommercialFilters(f)) return true;
  const block = getRecordCommercialBlock(row);
  if (!block) return false;
  if (f.status?.length) {
    const statuses = recordStatuses(row, block);
    if (!f.status.some((s) => statuses.has(s))) return false;
  }
  if (f.withOffers && block.offers.USD == null && block.offers.ARS == null) return false;
  if (f.unattended && !(block.unseen_inquiries_count > 0)) return false;
  if (f.staleDays) {
    const t = block.last_activity_at ? Date.parse(block.last_activity_at) : NaN;
    if (!Number.isFinite(t)) return false;
    if (now - t <= f.staleDays * DAY_MS) return false;
  }
  return true;
}

export const filterRecordsByCommercial = (rows, f, now = Date.now()) => (
  countActiveCommercialFilters(f)
    ? (Array.isArray(rows) ? rows : []).filter((row) => recordMatchesCommercialFilters(row, f, now))
    : rows
);

const stageRank = (status) => {
  const i = COMMERCIAL_STAGE_ORDER.indexOf(status);
  return i < 0 ? COMMERCIAL_STAGE_ORDER.length : i;
};
const activity = (block) => {
  const t = block?.last_activity_at ? Date.parse(block.last_activity_at) : NaN;
  return Number.isFinite(t) ? t : null;
};

/**
 * Comparador comercial. Sin bloque cuenta como 0 / sin fecha / sin etapa.
 * Empate → 0 (el caller aplica su desempate estable).
 */
export function compareRecordsCommercial(a, b, sortBy) {
  const ba = getRecordCommercialBlock(a);
  const bb = getRecordCommercialBlock(b);
  switch (sortBy) {
    case 'interested-desc':
      return ((bb?.leads_count || 0) - (ba?.leads_count || 0))
        || ((bb?.interests_count || 0) - (ba?.interests_count || 0));
    case 'commercial-inquiries-desc':
      return (bb?.inquiries_count || 0) - (ba?.inquiries_count || 0);
    case 'commercial-inquiries-asc':
      return (ba?.inquiries_count || 0) - (bb?.inquiries_count || 0);
    case 'activity-desc': {
      const ta = activity(ba);
      const tb = activity(bb);
      if (ta == null && tb == null) return 0;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return tb - ta;
    }
    case 'stage-desc':
      return stageRank(ba?.status || 'sin_interes') - stageRank(bb?.status || 'sin_interes');
    default:
      return 0;
  }
}

/** Orden estable: comercial y, en empate, el orden que ya traían las filas. */
export function sortRecordsCommercial(rows, sortBy) {
  const list = Array.isArray(rows) ? rows.map((row, i) => ({ row, i })) : [];
  list.sort((x, y) => compareRecordsCommercial(x.row, y.row, sortBy) || (x.i - y.i));
  return list.map((x) => x.row);
}
